import React, { useMemo, useRef, useState } from 'react';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import { ISplitFormFields } from '@/types/contentful';

import { usePageDataContext } from '../../../context/PageData';

import { trackWithFingerprint } from '../../../utils/trackWithFingerprint';
import { emailRegex } from '../../../utils/regex';

import RichText from '../../RichText';
import { EmbeddedInlineEntryNode } from '../../RichText/Nodes';

import { FormGroup } from './FormGroup';
import { FormField } from './FormField';
import { FormSubmit } from './FormSubmit';
import { FormSuccess } from './FormSuccess';

import {
  formFieldEnum,
  REQUIRED_FILED_MESSAGE,
  EMAIL_VALIDATION_MESSAGE,
  DATE_VALIDATION_MESSAGE
} from './shared';

import GlLink from '../../Link';

const flattenFormGroups = (formGroups: any) =>
  formGroups.reduce((curr: any, group: any) => {
    const contentType = group?.sys?.contentType?.sys?.id || undefined;

    if (contentType === 'formGroup') {
      return [...curr, ...group?.fields?.formFields];
    } else if (contentType) {
      return [...curr, group];
    }
  }, []);

export function Form({
  id,
  heading,
  subheading,
  formGroups,
  disclaimer,
  segmentSuccessEvent,
  submitButton,
  ...formProps
}: ISplitFormFields) {
  // Flatten form fields and form groups in to single array
  const flattenedGroups = useMemo(
    () => flattenFormGroups(formGroups),
    [formGroups]
  );

  // Map form field ids to state object
  const state = flattenedGroups.reduce((acc: any, { fields }: any) => {
    const { type } = fields ?? {};
    switch (type) {
      case formFieldEnum.CHECKBOX:
      case formFieldEnum.RADIO:
        return { ...acc, [fields.id]: false };

      case formFieldEnum.EMAIL:
      case formFieldEnum.PHONE:
      case formFieldEnum.TEXT:
      case formFieldEnum.NUMBER:
      case formFieldEnum.TEXTAREA:
      case formFieldEnum.DATE:
      case formFieldEnum.SELECT:
      default:
        return { ...acc, [fields.id]: '' };
    }
  }, {});

  // Check if any of the form fields are required
  const requiredFields = flattenedGroups.filter(
    ({ fields }: any) => fields?.required
  );

  const [submitted, setSubmitted] = useState(false);
  const [formState, setFormState] = useState<any>(state);
  const [formErrors, setFormErrors] = useState<any>({});

  const formRef = useRef<HTMLDivElement>(null);

  const { page } = usePageDataContext();

  const updateFormState = (key: string, val: any) => {
    setFormState((prevState: any) => ({ ...prevState, [key]: val }));
  };

  const validateForm = () => {
    const errors = flattenedGroups.reduce((acc: any, { fields }: any) => {
      const { type, id, required, errorMessage } = fields ?? {};

      const state = formState[id];
      const value = typeof state === 'string' ? state.trim() : state;

      if (!value && required) {
        return { ...acc, [id]: errorMessage || REQUIRED_FILED_MESSAGE };
      }

      switch (type) {
        case formFieldEnum.EMAIL:
          if (!value.match(emailRegex)) {
            return { ...acc, [id]: EMAIL_VALIDATION_MESSAGE };
          }
          return { ...acc };

        case formFieldEnum.DATE:
          const [month, day, year] = value.split(' / ').map(Number);
          const date = new Date(year, month - 1, day);
          if (
            date.getFullYear() !== year ||
            date.getMonth() + 1 !== month ||
            date.getDate() !== day
          ) {
            return { ...acc, [id]: DATE_VALIDATION_MESSAGE }; // Invalid date
          }
          const today = new Date();
          if (date >= today) {
            return { ...acc, [id]: DATE_VALIDATION_MESSAGE }; // Date is not in the past
          }
          return { ...acc };

        case formFieldEnum.CHECKBOX:
        case formFieldEnum.RADIO:
        case formFieldEnum.PHONE:
        case formFieldEnum.TEXT:
        case formFieldEnum.NUMBER:
        case formFieldEnum.TEXTAREA:
        case formFieldEnum.SELECT:
        default:
          return { ...acc };
      }
    }, {});

    // If there are no errors return null else return errors
    return Object.keys(errors).length ? errors : null;
  };

  const submitForm = () => {
    const errors = validateForm();

    if (errors) {
      setFormErrors(errors);
      trackWithFingerprint('Form Submit Failure', {
        ...formState,
        page_name: page?.title,
        page_url: window.location.href,
        page_slug: page?.slug
      });
      return;
    }

    // Reset form errors
    setFormErrors({});
    setSubmitted(true);
    trackWithFingerprint(
      segmentSuccessEvent ? segmentSuccessEvent : 'Form Submit Success',
      {
        ...formState,
        page_name: page?.title,
        page_url: window.location.href,
        page_slug: page?.slug
      }
    );

    formRef.current &&
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="m-3 flex h-fit w-full scroll-mt-[150px] flex-col items-center justify-center rounded-card-shape bg-white tablet:mx-0 tablet:my-4 desktop:mx-12"
      style={{
        boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.16)'
      }}
      id={id}
      ref={formRef}
      data-testid="split-module-form"
    >
      {submitted ? (
        <FormSuccess {...formProps} />
      ) : (
        <div className="flex w-full flex-col px-6 py-10 desktop:p-10">
          {heading && (
            <h4 className="typography-display-4 mb-2 text-center font-medium">
              {heading}
            </h4>
          )}

          {subheading && (
            <RichText
              data={subheading as any}
              overrideOptions={{
                [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                  <p className="text-center text-sm font-normal leading-[22px] text-black desktop:text-lg desktop:leading-[26px]">
                    {children}
                  </p>
                ),
                [INLINES.HYPERLINK]: (node, children) => (
                  <GlLink
                    href={node.data.uri}
                    className="text-black underline transition-colors duration-200 ease-in-out hover:text-navy desktop:hover:text-navy"
                  >
                    {children}
                  </GlLink>
                ),
                [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                  <EmbeddedInlineEntryNode
                    node={node}
                    colour="defaultText"
                    fontWeight="p"
                  >
                    {children}
                  </EmbeddedInlineEntryNode>
                )
              }}
            />
          )}

          <form className="mt-10 flex w-full flex-col">
            {formGroups.map(({ sys, fields }: any) =>
              sys?.contentType?.sys?.id === 'formGroup' ? (
                <FormGroup
                  {...fields}
                  key={sys?.id}
                  state={formState}
                  errors={formErrors}
                  handleOnChange={updateFormState}
                />
              ) : (
                <FormField
                  {...fields}
                  key={sys?.id}
                  state={formState}
                  errors={formErrors}
                  handleOnChange={updateFormState}
                />
              )
            )}
          </form>

          {requiredFields.length && (
            <p className="mb-4 px-2 py-0 text-sm font-normal leading-[18px] text-dark-gray desktop:text-xs">
              * Required
            </p>
          )}

          {disclaimer && (
            <RichText
              data={disclaimer as any}
              overrideOptions={{
                [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                  <p className="px-2 py-0 text-sm font-normal leading-[18px] text-dark-gray desktop:text-xs">
                    {children}
                  </p>
                ),
                [INLINES.HYPERLINK]: (node, children) => (
                  <GlLink
                    href={node.data.uri}
                    className="text-dark-gray underline transition-colors duration-200 ease-in-out hover:text-navy desktop:hover:text-green"
                  >
                    {children}
                  </GlLink>
                ),
                [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                  <EmbeddedInlineEntryNode
                    node={node}
                    colour="inputLabelText"
                    fontWeight="p"
                  >
                    {children}
                  </EmbeddedInlineEntryNode>
                )
              }}
            />
          )}

          <FormSubmit
            text={
              (submitButton as any)?.fields?.text ||
              (submitButton as any)?.fields?.buttonText
            }
            buttonColor={(submitButton as any)?.fields?.buttonColor}
            buttonHoverColor={(submitButton as any)?.fields?.buttonHoverColor}
            handleOnSubmit={submitForm}
          />
        </div>
      )}
    </div>
  );
}
